import React, { useEffect, useId, useMemo, useState } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import api from '../services/api';
import logoIconPng from './logo-icon.svg';

const STORAGE_KEYS = {
  token: 'vizumToken',
  user: 'vizumUser',
  rememberUser: 'vizumRememberUser',
  loginEmail: 'lumix_login_email'
};

const loginPhrases = [
  {
    title: 'É bom ter você de volta.',
    text: 'Acesse sua ótica e continue de onde parou, com OS, clientes, vendas e pós-venda no mesmo lugar.'
  },
  {
    title: 'Sua operação pronta para o dia.',
    text: 'Entre no painel para acompanhar pedidos, entregas, equipe, financeiro e as próximas ações da loja.'
  },
  {
    title: 'Venda com controle.',
    text: 'A Lumix ajuda sua ótica a organizar a rotina e manter contato com o cliente depois da entrega.'
  },
  {
    title: 'Da OS ao retorno do cliente.',
    text: 'Tudo fica mais simples quando a venda, o histórico e o pós-venda caminham juntos.'
  }
];

function normalizarLogin(valor) {
  const login = String(valor || '').trim();
  if (!login) return '';
  return login.includes('@') ? login.toLowerCase() : login;
}

function validarEmailOuUsuario(valor) {
  const login = String(valor || '').trim();
  if (login.length < 3) return false;
  return true;
}

function senhaValida(valor) {
  return String(valor || '').length >= 6;
}

function getRedirectPath(location) {
  try {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (!redirect) return '/home';
    if (!redirect.startsWith('/')) return '/home';
    if (redirect.startsWith('//')) return '/home';
    return redirect;
  } catch {
    return '/home';
  }
}

function lerLoginEmailDoCheckout() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.loginEmail);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.email || '';
  } catch {
    return '';
  }
}

function montarErroInteligente(error, contexto = 'login') {
  const status = error?.response?.status || error?.status || null;
  const backendMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
  const message = String(backendMessage || '').trim();

  if (!navigator.onLine) return 'Você está sem conexão com a internet. Verifique a rede e tente novamente.';
  if (message.includes('Network Error') || message.includes('Failed to fetch')) return 'Não consegui conectar com o servidor. Verifique sua conexão ou tente em instantes.';
  if (status === 400) return message || 'Informe e-mail e senha para continuar.';
  if (status === 401) return message || 'E-mail ou senha incorretos. Verifique e tente novamente.';
  if (status === 403) return message || 'Acesso bloqueado. Fale com o suporte.';
  if (status === 404) return message || 'Usuário não encontrado. Crie sua conta primeiro.';
  if (status >= 500) return message || 'O servidor encontrou um problema. Tente novamente em instantes.';
  if (contexto === 'google') return message || 'Não foi possível entrar com Google. Verifique seu e-mail.';

  return message || 'Não foi possível entrar agora. Tente novamente.';
}

function salvarSessaoLogin(data) {
  const token = data?.token;
  const user = data?.user;
  if (!token || !user) throw new Error('Resposta de login inválida. Token ou usuário ausente.');

  localStorage.setItem(STORAGE_KEYS.token, token);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  if (data?.empresa) localStorage.setItem('vizumEmpresa', JSON.stringify(data.empresa));
}

function limparSessaoAntiga() {
  localStorage.removeItem(STORAGE_KEYS.token);
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem('vizumEmpresa');
}

function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const userId = useId();
  const passwordId = useId();
  const rememberId = useId();

  const redirectPath = useMemo(() => getRedirectPath(location), [location]);

  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrarMe, setLembrarMe] = useState(true);

  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  
  // Novo estado para guardar o nome da pessoa
  const [nomeUsuario, setNomeUsuario] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [capsLockAtivo, setCapsLockAtivo] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const googleConfigurado = googleClientId && googleClientId !== 'SEU_CLIENT_ID_AQUI' && googleClientId.length > 20;

  const [phraseIndex, setPhraseIndex] = useState(0);
  const phraseAtual = loginPhrases[phraseIndex];

  useEffect(() => {
    const timer = setInterval(() => setPhraseIndex((prev) => (prev + 1) % loginPhrases.length), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const remembered = localStorage.getItem(STORAGE_KEYS.rememberUser);
    const checkoutEmail = lerLoginEmailDoCheckout();

    if (checkoutEmail) {
      setUsuario(checkoutEmail);
      setAviso('Encontramos uma conta ativa com esse e-mail. Entre para continuar usando a Lumix.');
      return;
    }
    if (remembered) {
      setUsuario(remembered);
      setLembrarMe(true);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    if (token && location.pathname === '/login' && !location.search) {
      navigate('/home', { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  function finalizarLogin(data) {
    salvarSessaoLogin(data);
    const loginNormalizado = normalizarLogin(usuario);

    if (lembrarMe && loginNormalizado) {
      localStorage.setItem(STORAGE_KEYS.rememberUser, loginNormalizado);
    } else {
      localStorage.removeItem(STORAGE_KEYS.rememberUser);
    }

    sessionStorage.removeItem(STORAGE_KEYS.loginEmail);

    // Captura o nome da pessoa que retornou da API (pega só o primeiro nome)
    const fullName = data?.user?.nome || data?.user?.name || '';
    const firstName = fullName.split(' ')[0];
    if (firstName) {
      setNomeUsuario(firstName);
    }

    setLoading(false);
    setGoogleLoading(false);
    setErro('');
    
    // Dispara a animação de Sucesso Premium
    setSucesso(true);

    // Aguarda a animação durar ~2.2 segundos para redirecionar o usuário
    setTimeout(() => {
      navigate(redirectPath, { replace: true });
    }, 2200);
  }

  async function handleLogin(event) {
    event.preventDefault();
    const login = normalizarLogin(usuario);
    setErro(''); setAviso(''); setSucesso(false);

    if (!validarEmailOuUsuario(login)) { setErro('Informe seu e-mail, usuário ou código de acesso para entrar.'); return; }
    if (!senhaValida(senha)) { setErro('A senha precisa ter pelo menos 6 caracteres.'); return; }

    try {
      setLoading(true);
      const response = await api.post('/login', { usuario: login, senha });
      finalizarLogin(response.data);
    } catch (err) {
      limparSessaoAntiga();
      setLoading(false); setGoogleLoading(false); setSucesso(false);
      setErro(montarErroInteligente(err));
    }
  }

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setErro(''); setAviso(''); setSucesso(false);
      try {
        setGoogleLoading(true); setLoading(true);
        const response = await api.post('/login/google', { access_token: tokenResponse.access_token });
        finalizarLogin(response.data);
      } catch (err) {
        limparSessaoAntiga(); setGoogleLoading(false); setLoading(false);
        setErro(montarErroInteligente(err, 'google'));
      }
    },
    onError: () => {
      setGoogleLoading(false); setLoading(false);
      setErro('O login com Google foi cancelado ou falhou. Tente novamente.');
    }
  });

  function handleGoogleClick() {
    if (!googleConfigurado) { setErro('Login com Google ainda não foi configurado neste ambiente.'); return; }
    loginWithGoogle();
  }

  const bloqueado = loading || sucesso || googleLoading;
  const loginPreenchido = validarEmailOuUsuario(usuario);
  const senhaPreenchida = senhaValida(senha);
  const podeEntrar = loginPreenchido && senhaPreenchida && !bloqueado;

  return (
    <>
      <style>{css}</style>

      {/* OVERLAY DE SUCESSO (ANIMAÇÃO PREMIUM) */}
      <div className={`success-overlay ${sucesso ? 'active' : ''}`}>
        <div className="success-content">
          <div className="logo-pulse-wrap logo-large">
            <img src={logoIconPng} alt="Lumix" />
          </div>
          
          <h2 className="success-title">
            {nomeUsuario ? `Bem-vindo(a) de volta, ${nomeUsuario}!` : 'Bem-vindo(a) de volta!'}
          </h2>
          
          <p className="success-subtitle">Preparando sua área de trabalho...</p>
          <div className="success-badge">
            <Icons.CheckCircle /> Autenticado com segurança
          </div>
        </div>
      </div>

      {/* LAYOUT PRINCIPAL (Aplica efeito de blur e shrink quando sucesso=true) */}
      <div className={`layout-login ${sucesso ? 'exiting' : ''}`}>
        
        {/* PAINEL LATERAL (VISUAL) */}
        <aside className="sidebar-login">
          <div className="sidebar-inner-login">
            <Link to="/" className="brand-logo">
              <div className="logo-pulse-wrap">
                <img src={logoIconPng} alt="Lumix" />
              </div>
              <span className="brand-name">
                Lumix&nbsp;
                <span style={{color:'#4285F4'}}>Ó</span>
                <span style={{color:'#EA4335'}}>t</span>
                <span style={{color:'#FBBC04'}}>i</span>
                <span style={{color:'#34A853'}}>c</span>
                <span style={{color:'#4285F4'}}>a</span>
                <span style={{color:'#EA4335'}}>s</span>
              </span>
            </Link>

            <div className="visual-phrase-container">
              <div className="visual-phrase slide-up" key={phraseAtual.title}>
                <h1>{phraseAtual.title}</h1>
                <p>{phraseAtual.text}</p>
              </div>
              
              <div className="phrase-dots" aria-hidden="true">
                {loginPhrases.map((item, index) => (
                  <span key={item.title} className={index === phraseIndex ? 'active' : ''} />
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* PAINEL PRINCIPAL (FORMULÁRIO) */}
        <main className="main-login">
          <div className="form-container">
            
            {/* Logo Mobile */}
            <div className="mobile-brand">
              <Link to="/" className="brand-logo">
                <div className="logo-pulse-wrap">
                  <img src={logoIconPng} alt="Lumix" />
                </div>
                <span className="brand-name">
                  Lumix&nbsp;
                  <span style={{color:'#4285F4'}}>Ó</span>
                  <span style={{color:'#EA4335'}}>t</span>
                  <span style={{color:'#FBBC04'}}>i</span>
                  <span style={{color:'#34A853'}}>c</span>
                  <span style={{color:'#4285F4'}}>a</span>
                  <span style={{color:'#EA4335'}}>s</span>
                </span>
              </Link>
            </div>

            <div className="form-header">
              <div className="login-eyebrow">Acesso ao sistema</div>
              <h2 className="hero-h1">Acesse sua conta</h2>
              <p className="hero-sub">Informe seus dados para acessar a plataforma.</p>
            </div>

            {aviso && !erro && !sucesso && (
              <div className="toast-message toast-warning fade-in">
                <Icons.Info /> <span>{aviso}</span>
              </div>
            )}

            {erro && !sucesso && (
              <div className="toast-message toast-error fade-in">
                <Icons.Alert /> <span>{erro}</span>
              </div>
            )}

            <button
              type="button"
              className="btn btn-outline btn-full btn-google"
              disabled={bloqueado || !googleConfigurado}
              onClick={handleGoogleClick}
            >
              {googleLoading ? <Icons.Spinner /> : <Icons.Google />}
              {googleLoading ? 'Conectando...' : 'Entrar com Google'}
            </button>

            {!googleConfigurado && (
              <small className="input-help text-center" style={{ display: 'block', marginTop: 8 }}>
                Login Google indisponível neste ambiente.
              </small>
            )}

            <div className="divider">
              <span>ou entre com seus dados</span>
            </div>

            <form onSubmit={handleLogin} className="form-content">
              <div className="input-group">
                <label htmlFor={userId}>E-mail, usuário ou código de acesso</label>
                <input
                  id={userId} type="text" inputMode="text" placeholder="voce@suaotica.com.br"
                  value={usuario}
                  onChange={(e) => { setUsuario(e.target.value); setErro(''); setAviso(''); }}
                  onBlur={() => setUsuario(normalizarLogin(usuario))}
                  required disabled={bloqueado} className="input-base"
                  autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                />
              </div>

              <div className="input-group">
                <div className="label-row">
                  <label htmlFor={passwordId}>Senha</label>
                  <Link to="/recuperar-senha" className="link-text">Esqueceu a senha?</Link>
                </div>
                <div className="password-field">
                  <input
                    id={passwordId} type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={senha}
                    onChange={(e) => { setSenha(e.target.value); setErro(''); }}
                    onKeyUp={(e) => setCapsLockAtivo(e.getModifierState?.('CapsLock') || false)}
                    onBlur={() => setCapsLockAtivo(false)}
                    required disabled={bloqueado} className="input-base" autoComplete="current-password"
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} disabled={bloqueado} tabIndex={-1}>
                    {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                  </button>
                </div>
                {capsLockAtivo && <small className="input-help-error">Caps Lock está ativado.</small>}
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input id={rememberId} type="checkbox" checked={lembrarMe} onChange={(e) => setLembrarMe(e.target.checked)} disabled={bloqueado} className="checkbox-input" />
                  Lembrar de mim
                </label>
              </div>

              <button type="submit" className="btn btn-primary btn-full btn-login-submit" disabled={!podeEntrar}>
                {loading ? <><Icons.Spinner /> Entrando...</> : 'Entrar'}
                <div className="btn-shine"></div>
              </button>

              <div className="register-prompt">
                Ainda não tem uma conta? <Link to="/checkout/start">Começar agora</Link>
              </div>
            </form>

            <div className="legal-row">
              <Link to="/termos-de-uso">Termos</Link>
              <Link to="/politica-de-privacidade">Privacidade</Link>
              <Link to="/politica-de-cookies">Cookies</Link>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default function Login() {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'SEU_CLIENT_ID_AQUI';
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginScreen />
    </GoogleOAuthProvider>
  );
}

/* ==========================================
   ÍCONES (Estilizados no Padrão Google/Premium)
========================================== */
const Icons = {
  Google: () => (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  ),
  Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg>,
  EyeOff: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19C5 19 1 12 1 12a20.3 20.3 0 0 1 5.06-5.94" /><path d="M9.9 4.24A10.6 10.6 0 0 1 12 4c7 0 11 8 11 8a20.8 20.8 0 0 1-3.17 4.19" /><path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" /><path d="M1 1l22 22" /></svg>,
  Spinner: () => <svg className="spinner-svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /><line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" /></svg>,
  CheckCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
  Alert: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  Info: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
};

/* ==========================================
   CSS PREMIUM & ANIMAÇÕES
========================================== */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    --bg: #ffffff;
    --surface: #ffffff;
    --soft-bg: #f7f9f9;

    --line: #e8ecec;
    --line-strong: #d8dfde;

    --text: #172020;
    --text-2: #374642;
    --muted: #74807d;
    --soft: #9aa3a1;

    --accent: #36564c;
    --accent-soft: #eef4f2;

    --primary: #1a73e8;
    --danger: #a63e36;
    --danger-soft: #fff6f5;
    --warning: #8a6410;
    --warning-soft: #fffaf0;
    --success: #2f6b57;
    --success-soft: #f2f7f5;

    --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html,
  body {
    margin: 0;
    min-height: 100%;
    background: #fff;
    color: var(--text);
    font-family: var(--font);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  body {
    overflow-x: hidden;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button,
  input {
    font: inherit;
  }

  button:focus-visible,
  input:focus-visible,
  a:focus-visible {
    outline: 2px solid rgba(54,86,76,.18);
    outline-offset: 2px;
  }

  /* =========================================================
     SUCESSO — FLAT
  ========================================================= */
  .success-overlay {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: grid;
    place-items: center;
    padding: 24px;
    background: rgba(255,255,255,.98);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .2s ease, visibility .2s ease;
  }

  .success-overlay.active {
    opacity: 1;
    visibility: visible;
    pointer-events: all;
  }

  .success-content {
    width: min(100%, 390px);
    padding: 28px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    transform: translateY(6px);
    transition: transform .2s ease;
  }

  .success-overlay.active .success-content {
    transform: none;
  }

  .logo-pulse-wrap {
    display: flex;
    flex-shrink: 0;
  }

  .logo-large {
    width: 54px;
    height: 54px;
    margin-bottom: 18px;
  }

  .logo-large img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .success-title {
    margin: 0 0 6px;
    color: var(--text);
    font-size: 21px;
    line-height: 1.25;
    font-weight: 750;
    letter-spacing: -.03em;
  }

  .success-subtitle {
    margin: 0 0 17px;
    color: var(--muted);
    font-size: 11px;
    line-height: 1.5;
    font-weight: 500;
  }

  .success-badge {
    min-height: 30px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #dce9e4;
    border-radius: 6px;
    background: var(--success-soft);
    color: var(--success);
    font-size: 9px;
    font-weight: 700;
  }

  /* =========================================================
     LAYOUT
  ========================================================= */
  .layout-login {
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(360px, 40%) minmax(0, 1fr);
    background: #fff;
    transition: opacity .2s ease;
  }

  .layout-login.exiting {
    opacity: 0;
  }

  .sidebar-login {
    position: relative;
    padding: 38px;
    display: flex;
    align-items: stretch;
    background: #f1f1f1;
    border-right: 1px solid #dadada;
    overflow: hidden;
  }

  .sidebar-login::before,
  .sidebar-login::after {
    display: none;
  }

  .sidebar-inner-login {
    width: 100%;
    max-width: 450px;
    min-height: calc(100vh - 76px);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }

  .brand-logo {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    color: var(--text);
  }

  .brand-logo img {
    width: 27px;
    height: 27px;
    object-fit: contain;
  }

  .brand-name {
    color: var(--text);
    font-size: 15px;
    line-height: 1;
    font-weight: 800;
    letter-spacing: -.035em;
    white-space: nowrap;
  }

  .visual-phrase-container {
    margin: auto 0;
    padding: 36px 0;
  }

  .visual-phrase {
    max-width: 390px;
  }

  .visual-phrase h1 {
    margin: 0 0 12px;
    color: var(--text);
    font-size: clamp(30px, 3.6vw, 42px);
    line-height: 1.06;
    font-weight: 750;
    letter-spacing: -.045em;
  }

  .visual-phrase p {
    margin: 0;
    max-width: 360px;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.65;
    font-weight: 500;
  }

  .phrase-dots {
    margin-top: 22px;
    display: flex;
    gap: 5px;
  }

  .phrase-dots span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #cfd5d4;
    transition: width .18s ease, background .18s ease;
  }

  .phrase-dots span.active {
    width: 18px;
    border-radius: 999px;
    background: var(--accent);
  }

  /* =========================================================
     FORM
  ========================================================= */
  .main-login {
    padding: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
  }

  .form-container {
    width: min(100%, 390px);
  }

  .mobile-brand {
    display: none;
  }

  .form-header {
    margin-bottom: 24px;
  }

  .login-eyebrow {
    margin-bottom: 7px;
    color: var(--muted);
    font-size: 8.5px;
    line-height: 1;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  .login-eyebrow-dot {
    display: none;
  }

  .hero-h1 {
    margin: 0 0 5px;
    color: var(--text);
    font-size: 24px;
    line-height: 1.15;
    font-weight: 750;
    letter-spacing: -.035em;
  }

  .hero-sub {
    margin: 0;
    color: var(--muted);
    font-size: 10.5px;
    line-height: 1.55;
    font-weight: 500;
  }

  /* =========================================================
     BUTTONS
  ========================================================= */
  .btn {
    position: relative;
    overflow: hidden;
    min-height: 42px;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: 1px solid var(--line-strong);
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 10.5px;
    font-weight: 700;
    box-shadow: none;
    transition: background .12s ease, border-color .12s ease, color .12s ease, opacity .12s ease;
  }

  .btn:hover:not(:disabled) {
    transform: none;
  }

  .btn:disabled {
    opacity: .46;
    cursor: not-allowed;
  }

  .btn-full {
    width: 100%;
  }

  .btn-primary {
    color: #fff;
    background: var(--accent);
    border-color: var(--accent);
    box-shadow: none;
  }

  .btn-primary:hover:not(:disabled) {
    background: #2e4a42;
    border-color: #2e4a42;
    box-shadow: none;
  }

  .btn-outline {
    color: var(--text-2);
    background: #fff;
    border-color: var(--line-strong);
    box-shadow: none;
  }

  .btn-outline:hover:not(:disabled) {
    color: var(--text);
    background: var(--soft-bg);
    border-color: #c9d0cf;
  }

  .btn-google {
    min-height: 42px;
  }

  .btn-login-submit {
    margin-top: 3px;
  }

  .btn-shine {
    display: none;
  }

  /* =========================================================
     DIVIDER / FIELDS
  ========================================================= */
  .divider {
    margin: 20px 0;
    display: flex;
    align-items: center;
    text-align: center;
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--line);
  }

  .divider span {
    padding: 0 10px;
    color: var(--soft);
    font-size: 8px;
    line-height: 1;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: .05em;
    white-space: nowrap;
  }

  .form-content {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .input-group label {
    color: var(--text-2);
    font-size: 9.5px;
    line-height: 1.3;
    font-weight: 700;
  }

  .label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .link-text {
    color: var(--accent);
    font-size: 8.5px;
    font-weight: 700;
  }

  .link-text:hover {
    text-decoration: underline;
  }

  .input-base {
    width: 100%;
    height: 42px;
    padding: 0 11px;
    border: 1px solid var(--line-strong);
    border-radius: 7px;
    outline: 0;
    background: #fff;
    color: var(--text);
    font-size: 10.5px;
    font-weight: 500;
    box-shadow: none;
    transition: border-color .12s ease, background .12s ease;
  }

  .input-base::placeholder {
    color: #a4adaa;
    font-weight: 400;
  }

  .input-base:hover:not(:disabled) {
    border-color: #c8d0ce;
  }

  .input-base:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 2px rgba(54,86,76,.08);
  }

  .input-base:disabled {
    background: #f5f6f6;
    color: var(--soft);
    cursor: not-allowed;
  }

  .password-field {
    position: relative;
    display: flex;
    align-items: center;
  }

  .password-field .input-base {
    padding-right: 42px;
  }

  .password-toggle {
    position: absolute;
    right: 3px;
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--soft);
    cursor: pointer;
  }

  .password-toggle:hover:not(:disabled) {
    color: var(--text-2);
    background: var(--soft-bg);
  }

  .form-options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: -1px;
  }

  .checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: var(--muted);
    font-size: 9.5px;
    font-weight: 600;
    cursor: pointer;
  }

  .checkbox-input {
    width: 14px;
    height: 14px;
    accent-color: var(--accent);
    cursor: pointer;
  }

  .register-prompt {
    margin-top: 5px;
    text-align: center;
    color: var(--muted);
    font-size: 9px;
    line-height: 1.5;
    font-weight: 500;
  }

  .register-prompt a {
    color: var(--accent);
    font-weight: 700;
  }

  .register-prompt a:hover {
    text-decoration: underline;
  }

  .legal-row {
    margin-top: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
  }

  .legal-row a {
    color: var(--soft);
    font-size: 8px;
    font-weight: 600;
  }

  .legal-row a:hover {
    color: var(--text-2);
  }

  /* =========================================================
     HELP / TOASTS
  ========================================================= */
  .input-help {
    color: var(--soft);
    font-size: 8px;
    line-height: 1.4;
    font-weight: 500;
  }

  .input-help-error {
    margin-top: 2px;
    color: var(--danger);
    font-size: 8px;
    line-height: 1.4;
    font-weight: 700;
  }

  .text-center {
    text-align: center;
  }

  .toast-message {
    margin-bottom: 15px;
    padding: 10px 11px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border: 1px solid;
    border-radius: 7px;
    font-size: 9px;
    line-height: 1.45;
    font-weight: 650;
    box-shadow: none;
  }

  .toast-message svg {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    margin-top: 1px;
  }

  .toast-error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: #ead8d5;
  }

  .toast-warning {
    color: var(--warning);
    background: var(--warning-soft);
    border-color: #eadfbf;
  }

  .spinner-svg {
    animation: spin .8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .fade-in {
    animation: fadeIn .18s ease both;
  }

  .slide-up {
    animation: fadeIn .18s ease both;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* =========================================================
     RESPONSIVE
  ========================================================= */
  @media (max-width: 960px) {
    .layout-login {
      grid-template-columns: 1fr;
    }

    .sidebar-login {
      display: none;
    }

    .main-login {
      min-height: 100vh;
      padding: 24px 18px;
      align-items: flex-start;
    }

    .form-container {
      width: min(100%, 420px);
      margin: 0 auto;
      padding-top: 4px;
    }

    .mobile-brand {
      display: flex;
      justify-content: flex-start;
      margin-bottom: 34px;
      padding-bottom: 14px;
      border-bottom: 1px solid var(--line);
    }

    .mobile-brand .brand-logo {
      position: static;
    }
  }

  @media (max-width: 600px) {
    .main-login {
      padding: 16px 14px 24px;
    }

    .form-container {
      width: 100%;
      max-width: 410px;
    }

    .mobile-brand {
      margin-bottom: 28px;
    }

    .brand-logo img {
      width: 25px;
      height: 25px;
    }

    .brand-name {
      font-size: 14px;
    }

    .hero-h1 {
      font-size: 22px;
    }

    .hero-sub {
      font-size: 10px;
    }

    .form-header {
      margin-bottom: 21px;
    }

    .success-content {
      padding: 24px 18px;
    }

    .success-title {
      font-size: 19px;
    }

    .success-subtitle {
      font-size: 10px;
    }
  }

  @media (max-width: 380px) {
    .main-login {
      padding-left: 12px;
      padding-right: 12px;
    }

    .hero-h1 {
      font-size: 21px;
    }

    .legal-row {
      gap: 11px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
    }
  }
`;
